"use client";

import React, {
  ComponentPropsWithRef,
  useCallback,
  useEffect,
  useState,
} from "react";
import { EmblaCarouselType } from "embla-carousel";

type UseDotPaginationType = {
  selectedIndex: number;
  scrollSnaps: number[];
  onDotPaginationClick: (index: number) => void;
};

/**
 * useDotPagination Hook
 *
 * Provides state and handlers for carousel dot pagination.
 * Tracks the active slide index, the list of scroll snaps,
 * and provides a function to navigate between slides.
 */
export const useDotPagination = (
  emblaApi: EmblaCarouselType | undefined,
): UseDotPaginationType => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  // Navigate to a specific slide when a dot is clicked
  const onDotPaginationClick = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  // Initialize the list of scroll snaps
  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  // Update the selected index on carousel selection
  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    // Defer state updates to avoid synchronous re-render
    queueMicrotask(() => {
      onInit(emblaApi);
      onSelect(emblaApi);
    });

    // Listen to carousel lifecycle events
    emblaApi.on("reInit", onInit).on("reInit", onSelect).on("select", onSelect);

    return () => {
      // Clean up event listeners on unmount
      emblaApi.off("reInit", onInit);
      emblaApi.off("reInit", onSelect);
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onInit, onSelect]);
  return {
    selectedIndex,
    scrollSnaps,
    onDotPaginationClick,
  };
};

type PropType = ComponentPropsWithRef<"button">;

/**
 * DotPagination Component
 *
 * Represents a single pagination dot used to navigate carousel slides.
 * Intended to be used with `useDotPagination` for interactive navigation.
 */
export const DotPagination = (props: PropType) => {
  const { children, ...restProps } = props;

  return (
    <button type="button" {...restProps}>
      {children}
    </button>
  );
};
